import React, {
  ReactElement,
  ReactNode,
  useCallback,
  useId,
  useMemo
} from 'react'
import cn from 'classnames'
import { ID } from 'types/entity'
import { AnimatePresence, motion } from 'framer-motion'
import { buildOptionsList } from '../inputs/helpers'
import { IconType } from 'core/components/icons/types'
import Icon from 'core/components/icons/Icon'

export interface StandardOptionData<T extends { token_id: ID }> {
  token_id: T['token_id']
  label: ReactNode
  subLabel?: string
  menuElement?: ReactElement
  prefix?: `${IconType}` | ReactElement
  suffix?: `${IconType}` | ReactElement
  disabled?: boolean
  bottomBorder?: boolean
}

export interface TwoDimensionalOptionData<T extends { token_id: ID }> {
  groupLabel: ReactNode
  items:
    | StandardOptionData<T>[]
    | ReactElement<{ value: T['token_id'] | string; onClick: VoidFunction }>[]
}

/**
 * @description The data structures that this component can accept to render an option item
 */
export type Option<T extends { token_id: ID }> =
  | string
  | number
  | StandardOptionData<T>
  | TwoDimensionalOptionData<T>
  | ReactElement<{
      value: T['token_id']
      onClick: VoidFunction
      disabled?: boolean
    }>

export interface MenuProps<T extends { token_id: ID }> {
  value?:
    | T['token_id']
    | string
    | number
    | Array<T['token_id'] | string | number>
    | null
  onChange?: (
    v:
      | T['token_id']
      | string
      | number
      | Array<T['token_id'] | string | number>
      | null
  ) => void
  options?: Option<T>[]
  className?: string
  highlightedIndex?: number
  variant?: 'default' | 'solid'
  header?: ReactNode
  footer?: ReactNode
}

const MenuItem = <T extends { token_id: ID }>({
  options = [],
  value,
  onChange = () => undefined,
  className,
  highlightedIndex,
  variant = 'default',
  header,
  footer
}: MenuProps<T>) => {
  const customId = useId()
  const listData = useMemo(() => buildOptionsList(options), [options])
  const allowMultipleSelections = Array.isArray(value)

  const valueSet = useMemo(() => {
    return value
      ? new Set(([] as Array<T['token_id'] | string | number>).concat(value))
      : new Set([] as Array<T['token_id'] | string | number>)
  }, [value])

  const onChangeCB = useCallback(
    (v: T['token_id'] | string | number) => {
      if (!allowMultipleSelections) {
        return onChange(v)
      }
      const clonedSet = new Set(valueSet)
      if (clonedSet.has(v)) {
        clonedSet.delete(v)
      } else {
        clonedSet.add(v)
      }
      onChange(Array.from(clonedSet))
    },
    [allowMultipleSelections, onChange, valueSet]
  )

  const optionsList = useMemo(() => {
    let itemCount = 0
    const variantStyles = {
      default: {
        groupLabel:
          'pt-4 pb-2 mb-2 border-bottom border-dark-1 text-sm pl-4 pr-4',
        menuItem:
          'flex items-center justify-between hover:bg-secondary-1 p-2 px-4 first:mt-0 '
      },
      solid: {
        groupLabel: 'bg-dark-1 py-2 px-4 font-medium',
        menuItem:
          'flex items-center justify-between hover:bg-secondary-1 p-2 !px-4 first:mt-0 '
      }
    }
    return listData.map((opt, i) => {
      if ('groupLabel' in opt)
        return (
          <li className={variantStyles[variant].groupLabel} key={i}>
            {opt.groupLabel}
          </li>
        )

      const isSelected = valueSet.has(opt?.token_id)

      // If the option is a tag element and is selected we need to show a different type of tag - primary variant
      const checkedTagsAsLabel =
        React.isValidElement(opt?.label) &&
        opt.label?.props?.className?.includes('tag') &&
        isSelected
          ? React.cloneElement(opt.label, {
              ...opt.label.props,
              variant: 'primary'
            })
          : null

      const index = itemCount++

      return (
        <li
          tabIndex={-1}
          id={`item-${i}`}
          key={opt.token_id ?? `item-${i}`}
          className={cn(
            variantStyles[variant].menuItem,
            opt.disabled
              ? 'cursor-not-allowed bg-dark-8 hover:bg-dark-8 text-dark-3'
              : 'cursor-pointer',
            highlightedIndex === index && 'bg-secondary-1',
            !!opt?.bottomBorder && 'border-b border-dark-2',
            'pl-4'
          )}
          onClick={opt.disabled ? undefined : () => onChangeCB(opt.token_id)}
        >
          <div className="flex items-center w-full">
            {opt.prefix && (
              <div className="mr-7">
                {typeof opt.prefix === 'string' ? (
                  <Icon name={opt.prefix} width={12} height={12} noWrapper />
                ) : (
                  opt.prefix
                )}
              </div>
            )}
            <div className="w-full">
              <span>{checkedTagsAsLabel ?? opt.menuElement ?? opt.label}</span>
              {opt.subLabel && (
                <p className="mt-1 text-dark-3">{opt.subLabel}</p>
              )}
            </div>
            {opt.suffix && (
              <div className="ml-7 justify-self-end">
                {typeof opt.suffix === 'string' ? (
                  <Icon name={opt.suffix} width={12} height={12} noWrapper />
                ) : (
                  opt.suffix
                )}
              </div>
            )}
          </div>
          {isSelected && (
            <div className="w-7 flex justify-end">
              <Icon name={'check'} width={12} height={12} noWrapper />
            </div>
          )}
        </li>
      )
    })
  }, [highlightedIndex, listData, onChangeCB, valueSet, variant])

  const renderHeaderFooter = useCallback((id: string, component: ReactNode) => {
    if (!component) return
    const isValidElement = React.isValidElement(component)
    return (
      <div
        className={cn(
          'hover:bg-secondary-1 cursor-pointer',
          isValidElement ? '' : 'py-2 px-4'
        )}
        id={id}
        tabIndex={0}
      >
        {isValidElement
          ? React.cloneElement(
              component as React.ReactElement<{ className: string }>,
              {
                className: cn(
                  (component as React.ReactElement<{ className: string }>)
                    ?.props?.className ?? '',
                  'py-2 px-4'
                )
              }
            )
          : component}
      </div>
    )
  }, [])

  const headerContent = useMemo(() => {
    return renderHeaderFooter(`menu_custom_header_${customId}`, header)
  }, [customId, header, renderHeaderFooter])

  const footerContent = useMemo(() => {
    return renderHeaderFooter(`menu_custom_footer_${customId}`, footer)
  }, [customId, footer, renderHeaderFooter])

  return (
    <div className="card bg-white p-0 min-w-fit">
      {headerContent}
      <AnimatePresence initial={false} mode="popLayout">
        <motion.ol
          className={cn(
            'overflow-x-hidden overflow-y-auto max-h-80',
            className
          )}
          id={`menu_scrollable_list_${customId}`}
        >
          {optionsList}
        </motion.ol>
      </AnimatePresence>
      {footerContent}
    </div>
  )
}

export default MenuItem
